// Configurazione centrale dell'applicazione J-O-H

/**
 * Base URL delle API. Vuoto = same-origin (Vercel: /api/*).
 * In locale `.env` può puntare a http://localhost:3001.
 * Eventuale URL Render residua viene ignorata.
 */
const resolveApiUrl = (): string => {
  const raw = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  if (!raw || raw.includes('onrender.com')) {
    return '';
  }
  return raw;
};

export const getApiEndpoint = (path: string): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${resolveApiUrl()}${normalized}`;
};

export const config = {
  // Supabase
  supabase: {
    url: process.env.REACT_APP_SUPABASE_URL || '',
    anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY || ''
  },

  // Stripe
  stripe: {
    publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...',
    currency: 'eur',
    country: 'IT'
  },

  // Email (Resend - invio gestito interamente lato server per sicurezza)
  email: {
    provider: 'resend',
    from: {
      name: 'J-O-H - I tuoi alloggi da sogno',
      address: process.env.REACT_APP_RESEND_FROM || 'no-reply@dhevylgroup.com'
    }
  },

  // App
  app: {
    name: 'J-O-H',
    url: process.env.REACT_APP_SITE_URL || 'https://j-o-h.com',
    apiUrl: resolveApiUrl(),
    supportEmail: 'info@dhevylgroup.com',
    version: '1.0.0'
  },

  // Feature flags
  features: {
    registration: process.env.REACT_APP_ENABLE_REGISTRATION !== 'false',
    booking: process.env.REACT_APP_ENABLE_BOOKING !== 'false',
    payments: process.env.REACT_APP_ENABLE_PAYMENTS !== 'false',
    emailVerification: true,
    phoneVerification: true,
    documentVerification: true,
    socialLogin: true
  },

  // Pricing
  pricing: {
    cleaningFeePercentage: 0.1, // 10%
    serviceFeePercentage: 0.05, // 5%
    taxPercentage: 0.1, // 10%
    currency: 'EUR',
    locale: 'it-IT'
  },

  // Validation
  validation: {
    minAge: 18,
    maxUploadSize: 5242880, // 5MB
    allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    passwordMinLength: 8,
    phoneVerificationCodeLength: 6,
    phoneVerificationCodeExpiry: 600000 // 10 minutes in ms
  },

  // Integrations
  integrations: {
    airbnb: {
      enabled: false, // Da implementare
      apiUrl: 'https://api.airbnb.com'
    },
    avaibook: {
      enabled: false, // Da implementare
      apiUrl: 'https://api.avaibook.com'
    }
  },

  // Storage buckets
  storage: {
    images: 'images',
    documents: 'documents',
    buckets: {
      accommodations: 'images/accommodations',
      profiles: 'images/profiles',
      documents: 'documents/identity',
      uploads: 'uploads'
    }
  },

  // URLs e routing
  routes: {
    home: '/',
    register: '/register',
    login: '/login',
    dashboard: '/dashboard',
    profile: '/profile',
    accommodations: '/accommodations',
    verifyEmail: '/verify-email',
    resetPassword: '/reset-password',
    admin: '/admin',
    termsOfService: '/terms-of-service',
    privacyPolicy: '/privacy-policy'
  },

  // UI/UX
  ui: {
    colors: {
      primary: '#004744',
      primaryHover: '#004744',
      success: '#004744',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6'
    },
    animations: {
      duration: {
        fast: 200,
        normal: 300,
        slow: 500
      }
    },
    breakpoints: {
      mobile: '768px',
      tablet: '1024px',
      desktop: '1200px'
    }
  },

  // Limiti e quotas
  limits: {
    maxBookingsPerUser: 10,
    maxImagesPerAccommodation: 10,
    maxDocumentsPerUser: 5,
    rateLimiting: {
      emailVerification: 3, // max 3 email per ora
      phoneVerification: 5, // max 5 SMS per ora
      registration: 1 // max 1 registrazione per IP per ora
    }
  }
};

// Utility per verificare se una feature è abilitata
export const isFeatureEnabled = (feature: keyof typeof config.features): boolean => {
  return config.features[feature];
};

// Utility per costruire URLs
export const buildUrl = (path: string): string => {
  return `${config.app.url}${path}`;
};

// Utility per formattare prezzi
export const formatPrice = (amount: number): string => {
  return new Intl.NumberFormat(config.pricing.locale, {
    style: 'currency',
    currency: config.pricing.currency
  }).format(amount);
};

// Validation helpers
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): boolean => {
  return password.length >= config.validation.passwordMinLength;
};

export const validateFileSize = (file: File): boolean => {
  return file.size <= config.validation.maxUploadSize;
};

export const validateFileType = (file: File): boolean => {
  return config.validation.allowedImageTypes.includes(file.type);
};

// Environment check
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

// Debug utility
export const debugLog = (...args: any[]): void => {
  if (isDevelopment()) {
    console.log('[J-O-H Debug]', ...args);
  }
};

export default config; 